import { trpc } from "@/lib/trpc";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import type { Plan, SubscriptionStatus } from "@deepguide-ai/types";
export type { Plan, SubscriptionStatus } from "@deepguide-ai/types";

export interface SubscriptionInfo {
  plan: Plan;
  status: SubscriptionStatus;
  trial: boolean;
  trialEnd: Date | null;
  currentPeriodEnd: Date | null;
  trialDaysLeft: number | null;
}

/**
 * Hook to manage subscription state and actions
 */
export function useSubscription() {
  const queryOptions = trpc.subscription.current.queryOptions();
  
  const { data, isLoading, refetch } = useQuery({
    ...queryOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
  
  const createCheckoutOptions = trpc.subscription.createCheckout.mutationOptions();
  const createCheckout = useMutation({
    ...createCheckoutOptions,
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create checkout session");
    },
  });
  
  const openPortalOptions = trpc.subscription.portal.mutationOptions();
  const openPortal = useMutation({
    ...openPortalOptions,
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to open billing portal");
    },
  });
  
  return {
    subscription: data || {
      plan: "STARTER" as Plan,
      status: "ACTIVE" as SubscriptionStatus,
      trial: false,
      trialEnd: null,
      currentPeriodEnd: null,
      trialDaysLeft: null,
    },
    loading: isLoading,
    isTrialing: data?.trial ?? false,
    trialDaysLeft: data?.trialDaysLeft ?? null,
    
    // Actions
    upgrade: async (plan: Plan, options?: {
      interval?: "monthly" | "annual";
      trialDays?: number;
      allowPromotionCodes?: boolean;
    }) => {
      return createCheckout.mutateAsync({
        plan,
        interval: options?.interval,
        trialDays: options?.trialDays,
        allowPromotionCodes: options?.allowPromotionCodes,
      });
    },
    
    manageSubscription: async () => {
      return openPortal.mutateAsync({});
    },
    
    refetch,
  };
}

/**
 * Hook to get subscription usage statistics
 */
export function useSubscriptionUsage(options?: {
  startDate?: Date;
  endDate?: Date;
}) {
  const queryOptions = trpc.subscription.usage.queryOptions({
    startDate: options?.startDate,
    endDate: options?.endDate,
  });
  
  const { data, isLoading, refetch } = useQuery({
    ...queryOptions,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000,
  });
  
  return {
    usage: data?.usage,
    features: data?.features ?? [],
    plan: data?.plan ?? ("STARTER" as Plan),
    loading: isLoading,
    refetch,
  };
}
